import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/useAuthStore';
import {
  addTicketResponseNote,
  archiveTicket,
  assignTicket,
  claimTicket,
  createTicketNote,
  delegateTicket,
  deleteTicket,
  deleteTicketAttachment,
  getTicketActivityLog,
  getTicketAttachments,
  getTicketById,
  getTicketCounts,
  getTicketEmails,
  getTicketNotes,
  getTickets,
  markTicketAsSeen,
  sendTicketReply,
  setTicketReferent,
  unassignTicket,
  updateTicketCategoryOnTicket,
  updateTicketClientFields,
  updateTicketStatus,
  uploadTicketAttachment,
} from '../../services/tickets/ticketService';
import type { TicketFilters } from '../../services/tickets/ticketService';
import { STATIC } from '../../lib/queryPresets';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: (filters: TicketFilters) => ['tickets', 'list', filters] as const,
  detail: (id: string) => ['tickets', 'detail', id] as const,
  activity: (ticketId: string) => ['tickets', 'activity', ticketId] as const,
  notes: (ticketId: string) => ['tickets', 'notes', ticketId] as const,
  attachments: (ticketId: string) => ['tickets', 'attachments', ticketId] as const,
  counts: () => ['tickets', 'counts'] as const,
  emails: (ticketId: string) => [...ticketKeys.all, 'emails', ticketId] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

export function useTickets(filters: TicketFilters) {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.list(filters),
    queryFn: async () => {
      const results = await getTickets(filters);
      let out = results;
      // Handle 'me' filter client-side
      if (filters.assigned_to === 'me' && collaboratorId) {
        out = out.filter((t) => t.assigned_to === collaboratorId);
      }
      if (filters.referent_of_me && collaboratorId) {
        out = out.filter(
          (t) => t.referent_id === collaboratorId && !t.assigned_to,
        );
      }
      return out;
    },
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.detail(id ?? ''),
    queryFn: () => getTicketById(id!),
    enabled: Boolean(id),
  });
}

export function useTicketActivityLog(ticketId: string | undefined) {
  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.activity(ticketId ?? ''),
    queryFn: () => getTicketActivityLog(ticketId!),
    enabled: Boolean(ticketId),
  });
}

export function useTicketCounts() {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.counts(),
    queryFn: () => getTicketCounts(collaboratorId ?? undefined),
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useAssignTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({
      ticketId,
      collaboratorId: targetId,
      message,
    }: {
      ticketId: string;
      collaboratorId: string;
      message?: string;
    }) => assignTicket(ticketId, targetId, collaboratorId!, message),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Ticket assigné avec succès');
    },
    onError: () => {
      toast.error("Erreur lors de l'assignation du ticket");
    },
  });
}

export function useSetTicketReferent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, referentId }: { ticketId: string; referentId: string | null }) =>
      setTicketReferent(ticketId, referentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Référent mis à jour');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour du référent');
    },
  });
}

export function useUnassignTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (ticketId: string) => unassignTicket(ticketId, collaboratorId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Assignation retirée');
    },
    onError: () => {
      toast.error("Erreur lors du retrait de l'assignation");
    },
  });
}

export function useClaimTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (ticketId: string) => claimTicket(ticketId, collaboratorId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Ticket pris en charge');
    },
    onError: () => {
      toast.error('Erreur lors de la prise en charge');
    },
  });
}

export function useUpdateTicketStatus() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: string }) =>
      updateTicketStatus(ticketId, status, collaboratorId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Statut mis à jour');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour du statut');
    },
  });
}


export function useMarkTicketAsSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => markTicketAsSeen(ticketId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.counts() });
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => deleteTicket(ticketId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Ticket supprimé');
    },
    onError: () => {
      toast.error('Erreur lors de la suppression du ticket');
    },
  });
}

export function useArchiveTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (ticketId: string) => archiveTicket(ticketId, collaboratorId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('Ticket archivé');
    },
    onError: () => {
      toast.error("Erreur lors de l'archivage du ticket");
    },
  });
}

export function useAddTicketResponseNote() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, note }: { ticketId: string; note: string }) =>
      addTicketResponseNote(ticketId, note, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({
        queryKey: ticketKeys.activity(ticketId),
      });
      toast.success('Note enregistrée');
    },
    onError: () => {
      toast.error("Erreur lors de l'enregistrement de la note");
    },
  });
}

export function useUpdateTicketCategoryOnTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, categoryId }: { ticketId: string; categoryId: string | null }) =>
      updateTicketCategoryOnTicket(ticketId, categoryId, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.list({}) });
      toast.success('Catégorie mise à jour');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour de la catégorie');
    },
  });
}

export function useUpdateTicketClientFields() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({
      ticketId,
      fields,
    }: {
      ticketId: string;
      fields: { client_status?: string | null };
    }) => updateTicketClientFields(ticketId, fields, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.list({}) });
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour');
    },
  });
}

export function useTicketNotes(ticketId: string | undefined) {
  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.notes(ticketId ?? ''),
    queryFn: () => getTicketNotes(ticketId!),
    enabled: Boolean(ticketId),
  });
}

export function useCreateTicketNote() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, content }: { ticketId: string; content: string }) =>
      createTicketNote(ticketId, content, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.notes(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
    },
    onError: () => {
      toast.error("Erreur lors de l'ajout de la note");
    },
  });
}

export function useDelegateTicket() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, delegateToId }: { ticketId: string; delegateToId: string }) =>
      delegateTicket(ticketId, delegateToId, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.list({}) });
      toast.success('Ticket délégué');
    },
    onError: () => {
      toast.error('Erreur lors de la délégation');
    },
  });
}

export function useTicketAttachments(ticketId: string | undefined) {
  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.attachments(ticketId ?? ''),
    queryFn: () => getTicketAttachments(ticketId!),
    enabled: Boolean(ticketId),
  });
}

export function useUploadTicketAttachment() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: string; file: File }) =>
      uploadTicketAttachment(ticketId, file, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.attachments(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
      toast.success('Pièce jointe ajoutée');
    },
    onError: () => {
      toast.error("Erreur lors de l'upload de la pièce jointe");
    },
  });
}

export function useDeleteTicketAttachment() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: ({
      attachmentId,
      storagePath,
      ticketId,
    }: {
      attachmentId: string;
      storagePath: string;
      ticketId: string;
    }) => deleteTicketAttachment(attachmentId, storagePath, ticketId, collaboratorId!),
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.attachments(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
      toast.success('Pièce jointe supprimée');
    },
    onError: () => {
      toast.error('Erreur lors de la suppression de la pièce jointe');
    },
  });
}

export function useTicketEmails(ticketId: string | undefined) {
  return useQuery({
    ...STATIC, // useTicketsRealtime couvre déjà → pas de polling doublon
    queryKey: ticketKeys.emails(ticketId ?? ''),
    queryFn: () => getTicketEmails(ticketId!),
    enabled: Boolean(ticketId),
  });
}

export function useSendTicketReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendTicketReply,
    onSuccess: (_data, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.emails(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ticketKeys.activity(ticketId) });
      toast.success('Message envoyé au candidat');
    },
    onError: (err: Error) => {
      toast.error(err.message || "L'envoi a échoué");
    },
  });
}
